/**
 * useDecisions Hook - Phase 5 Epic 5.3
 * Manages human-in-the-loop decision workflows
 */

import { useState, useEffect, useCallback } from 'react';
import { useWebSocketContext } from '@/contexts/WebSocketContext';
import axios from 'axios';

// Get API base URL - same pattern as client.ts
const getAPIBaseURL = (): string => {
  // In production, always use Railway
  if (import.meta.env.PROD) {
    return 'https://marketing-ai-platform-production.up.railway.app';
  }
  // In development, use environment variable or localhost
  return import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000';
};

const API_BASE_URL = getAPIBaseURL();

export type DecisionType =
  | 'budget_reallocation'
  | 'campaign_launch'
  | 'performance_alert'
  | 'agent_action'
  | 'ml_override'
  | 'content_approval'
  | 'spend_threshold';

export type DecisionPriority = 'critical' | 'high' | 'medium' | 'low';
export type DecisionStatus = 'pending' | 'approved' | 'rejected' | 'expired' | 'escalated';

export interface Decision {
  id: string;
  type: DecisionType;
  priority: DecisionPriority;
  status: DecisionStatus;
  title: string;
  description: string;
  aiReasoning?: string;
  suggestedAction?: string;
  requestData: any;
  currentState?: any;
  predictedImpact?: any;
  entityType?: string;
  entityId?: string;
  campaignId?: string;
  agentId?: string;
  brandId?: string;
  workflowId?: string;
  requiredApprovers: number;
  currentApprovers: number;
  approvalThreshold?: string;
  assignedTo: string[];
  assignedRole?: string;
  decidedBy?: string;
  decidedAt?: string;
  decision?: string;
  decisionReason?: string;
  decisionData?: any;
  escalated: boolean;
  escalatedAt?: string;
  escalatedTo?: string;
  escalationReason?: string;
  expiresAt?: string;
  autoDecision?: string;
  createdAt: string;
  updatedAt: string;
  approvals?: DecisionApproval[];
  history?: DecisionHistoryItem[];
}

export interface DecisionApproval {
  id: string;
  decisionId: string;
  userId: string;
  userName: string;
  userRole: string;
  action: string;
  comment?: string;
  metadata?: any;
  createdAt: string;
}

export interface DecisionHistoryItem {
  id: string;
  decisionId: string;
  event: string;
  description?: string;
  actorId?: string;
  actorName?: string;
  previousState?: any;
  newState?: any;
  metadata?: any;
  timestamp: string;
}

export interface UseDecisionsOptions {
  userId?: string;
  autoFetch?: boolean;
  status?: DecisionStatus;
  type?: DecisionType;
  priority?: DecisionPriority;
  limit?: number;
}

export interface UseDecisionsReturn {
  // State
  decisions: Decision[];
  pendingCount: number;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchDecisions: () => Promise<void>;
  fetchPendingCount: () => Promise<void>;
  fetchDecision: (decisionId: string) => Promise<Decision | null>;
  approveDecision: (decisionId: string, comment?: string) => Promise<void>;
  rejectDecision: (decisionId: string, reason?: string) => Promise<void>;
  escalateDecision: (decisionId: string, escalatedTo: string, reason: string) => Promise<void>;
  clearError: () => void;

  // Filters
  getDecisionsByType: (type: DecisionType) => Decision[];
  getDecisionsByPriority: (priority: DecisionPriority) => Decision[];
  getPendingDecisions: () => Decision[];
  getCriticalDecisions: () => Decision[];
}

export function useDecisions(options: UseDecisionsOptions = {}): UseDecisionsReturn {
  const {
    userId = 'anonymous',
    autoFetch = true,
    status,
    type,
    priority,
    limit = 50,
  } = options;

  const { socket, isConnected } = useWebSocketContext();

  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch decisions from API
  const fetchDecisions = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        userId,
        ...(status && { status }),
        ...(type && { type }),
        ...(priority && { priority }),
        ...(limit && { limit: limit.toString() }),
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/v1/decisions?${params.toString()}`
      );

      if (response.data.success) {
        setDecisions(response.data.data);
      } else {
        setError('Failed to fetch decisions');
      }
    } catch (err: any) {
      console.error('Error fetching decisions:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch decisions');
    } finally {
      setIsLoading(false);
    }
  }, [userId, status, type, priority, limit]);

  // Fetch pending count
  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/decisions/pending-count?userId=${userId}`
      );

      if (response.data.success) {
        setPendingCount(response.data.data.count);
      }
    } catch (err: any) {
      console.error('Error fetching pending count:', err);
    }
  }, [userId]);

  // Fetch single decision
  const fetchDecision = useCallback(async (decisionId: string): Promise<Decision | null> => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/v1/decisions/${decisionId}`
      );

      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (err: any) {
      console.error('Error fetching decision:', err);
      setError(err.response?.data?.error?.message || 'Failed to fetch decision');
      return null;
    }
  }, []);

  // Approve decision
  const approveDecision = useCallback(
    async (decisionId: string, comment?: string) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/decisions/${decisionId}/approve`,
          {
            userId,
            userName: 'Demo User',
            userRole: 'admin',
            comment,
          }
        );

        if (response.data.success) {
          // Update local state
          setDecisions((prev) =>
            prev.map((d) =>
              d.id === decisionId ? response.data.data : d
            )
          );

          // Refresh pending count
          await fetchPendingCount();
        }
      } catch (err: any) {
        console.error('Error approving decision:', err);
        setError(err.response?.data?.error?.message || 'Failed to approve decision');
        throw err;
      }
    },
    [userId, fetchPendingCount]
  );

  // Reject decision
  const rejectDecision = useCallback(
    async (decisionId: string, reason?: string) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/decisions/${decisionId}/reject`,
          {
            userId,
            userName: 'Demo User',
            userRole: 'admin',
            reason,
          }
        );

        if (response.data.success) {
          // Update local state
          setDecisions((prev) =>
            prev.map((d) =>
              d.id === decisionId ? response.data.data : d
            )
          );

          // Refresh pending count
          await fetchPendingCount();
        }
      } catch (err: any) {
        console.error('Error rejecting decision:', err);
        setError(err.response?.data?.error?.message || 'Failed to reject decision');
        throw err;
      }
    },
    [userId, fetchPendingCount]
  );

  // Escalate decision
  const escalateDecision = useCallback(
    async (decisionId: string, escalatedTo: string, reason: string) => {
      try {
        const response = await axios.post(
          `${API_BASE_URL}/api/v1/decisions/${decisionId}/escalate`,
          {
            escalatedBy: userId,
            escalatedTo,
            reason,
          }
        );

        if (response.data.success) {
          // Update local state
          setDecisions((prev) =>
            prev.map((d) =>
              d.id === decisionId ? response.data.data : d
            )
          );
        }
      } catch (err: any) {
        console.error('Error escalating decision:', err);
        setError(err.response?.data?.error?.message || 'Failed to escalate decision');
        throw err;
      }
    },
    [userId]
  );

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Filter functions
  const getDecisionsByType = useCallback(
    (filterType: DecisionType) => {
      return decisions.filter((d) => d.type === filterType);
    },
    [decisions]
  );

  const getDecisionsByPriority = useCallback(
    (filterPriority: DecisionPriority) => {
      return decisions.filter((d) => d.priority === filterPriority);
    },
    [decisions]
  );

  const getPendingDecisions = useCallback(() => {
    return decisions.filter((d) => d.status === 'pending');
  }, [decisions]);

  const getCriticalDecisions = useCallback(() => {
    return decisions.filter((d) => d.priority === 'critical' && d.status === 'pending');
  }, [decisions]);

  // WebSocket event handlers
  useEffect(() => {
    if (!socket || !isConnected) return;

    // New decision created
    const handleDecisionCreated = (data: Decision) => {
      setDecisions((prev) => [data, ...prev]);
      setPendingCount((prev) => prev + 1);
    };

    // Decision updated
    const handleDecisionUpdated = (data: Decision) => {
      setDecisions((prev) =>
        prev.map((d) => (d.id === data.id ? data : d))
      );
    };

    // Decision approved
    const handleDecisionApproved = (data: Decision) => {
      setDecisions((prev) =>
        prev.map((d) => (d.id === data.id ? data : d))
      );
      setPendingCount((prev) => Math.max(0, prev - 1));
    };

    // Decision rejected
    const handleDecisionRejected = (data: Decision) => {
      setDecisions((prev) =>
        prev.map((d) => (d.id === data.id ? data : d))
      );
      setPendingCount((prev) => Math.max(0, prev - 1));
    };

    // Decision escalated
    const handleDecisionEscalated = (data: Decision) => {
      setDecisions((prev) =>
        prev.map((d) => (d.id === data.id ? data : d))
      );
    };

    // Register event listeners
    socket.on('decision:created', handleDecisionCreated);
    socket.on('decision:updated', handleDecisionUpdated);
    socket.on('decision:approved', handleDecisionApproved);
    socket.on('decision:rejected', handleDecisionRejected);
    socket.on('decision:escalated', handleDecisionEscalated);

    return () => {
      socket.off('decision:created', handleDecisionCreated);
      socket.off('decision:updated', handleDecisionUpdated);
      socket.off('decision:approved', handleDecisionApproved);
      socket.off('decision:rejected', handleDecisionRejected);
      socket.off('decision:escalated', handleDecisionEscalated);
    };
  }, [socket, isConnected]);

  // Auto-fetch on mount
  useEffect(() => {
    if (autoFetch) {
      fetchDecisions();
      fetchPendingCount();
    }
  }, [autoFetch, fetchDecisions, fetchPendingCount]);

  return {
    // State
    decisions,
    pendingCount,
    isLoading,
    error,

    // Actions
    fetchDecisions,
    fetchPendingCount,
    fetchDecision,
    approveDecision,
    rejectDecision,
    escalateDecision,
    clearError,

    // Filters
    getDecisionsByType,
    getDecisionsByPriority,
    getPendingDecisions,
    getCriticalDecisions,
  };
}
